import { TopNav } from "@dashboard/components/AppLayout";
import SearchInput from "@dashboard/components/AppLayout/ListFilters/components/SearchInput";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import {
  ExtensionsListUrlDialog,
  ExtensionsListUrlQueryParams,
  ExtensionsUrls,
} from "@dashboard/extensions/urls";
import { useInstalledExtensionsFilter } from "@dashboard/extensions/views/InstalledExtensions/hooks/useInstalledExtensionsFilter";
import { useHasManagedAppsPermission } from "@dashboard/hooks/useHasManagedAppsPermission";
import useNavigator from "@dashboard/hooks/useNavigator";
import createDialogActionHandlers from "@dashboard/utils/handlers/dialogActionHandlers";
import { useOnboarding } from "@dashboard/welcomePage/WelcomePageOnboarding/onboardingContext";
import { Box } from "@saleor/macaw-ui-next";
import React, { useEffect } from "react";

import { AddExtensionDropdown } from "./components/AddExtensionDropdown";
import { DeleteFailedInstallationDialog } from "./components/DeleteFailedInstallationDialog";
import { InstalledExtensionsList } from "./components/InstalledExtensionsList";
import { ThirdPartyIntegrationsSection } from "./components/ThirdPartyIntegrationsSection";
import { useInstalledExtensions } from "./hooks/useInstalledExtensions";
import { usePendingInstallation } from "./hooks/usePendingInstallation";
import { useThirdPartyIntegrations } from "./hooks/useThirdPartyIntegrations";

export interface InstalledExtensionsProps {
  params: ExtensionsListUrlQueryParams;
}

export const InstalledExtensions = ({ params }: InstalledExtensionsProps) => {
  const navigate = useNavigator();
  const { hasManagedAppsPermission } = useHasManagedAppsPermission();
  const { markOnboardingStepAsCompleted } = useOnboarding();

  useEffect(() => {
    markOnboardingStepAsCompleted("view-extensions");
  }, [markOnboardingStepAsCompleted]);

  const [openModal, closeModal] = createDialogActionHandlers<
    ExtensionsListUrlDialog,
    ExtensionsListUrlQueryParams
  >(navigate, ExtensionsUrls.resolveInstalledExtensionsUrl, {});

  const handleRemoveFailedInstallation = (id: string) => {
    openModal("app-installation-remove", { id });
  };

  const { installedExtensions, installedAppsLoading, refetchInstalledApps } =
    useInstalledExtensions();
  const { query, handleQueryChange, filteredInstalledExtensions } =
    useInstalledExtensionsFilter(installedExtensions);

  const {
    pendingInstallations,
    deleteInProgressAppStatus,
    pendingInstallationsLoading,
    handleRemoveInProgress,
  } = usePendingInstallation({
    searchQuery: query,
    onCloseModal: closeModal,
    refetchExtensions: refetchInstalledApps,
    onFailedInstallationRemove: handleRemoveFailedInstallation,
  });

  const { data: thirdPartyData, loading: thirdPartyLoading, error: thirdPartyError } = useThirdPartyIntegrations();

  return (
    <ListPageLayout>
      <TopNav
        withoutBorder
        isAlignToRight
        title="Application configurations"
      >
        {hasManagedAppsPermission && <AddExtensionDropdown />}
      </TopNav>
      <DashboardCard paddingX={6}>
        <Box paddingY={4}>
          <Box __width="370px">
            <SearchInput
              size="medium"
              initialSearch={query}
              placeholder="Search applications"
              onSearchChange={newQuery => handleQueryChange(newQuery)}
            />
          </Box>
        </Box>

        <InstalledExtensionsList
          installedExtensions={[...pendingInstallations, ...filteredInstalledExtensions]}
          loading={pendingInstallationsLoading || installedAppsLoading}
          clearSearch={() => handleQueryChange("")}
          searchQuery={query}
        />

        <ThirdPartyIntegrationsSection
          data={thirdPartyData}
          loading={thirdPartyLoading}
          error={thirdPartyError}
        />

        <DeleteFailedInstallationDialog
          confirmButtonState={deleteInProgressAppStatus}
          name={pendingInstallations.find(installation => installation.id === params?.id)?.name}
          onClose={closeModal}
          onConfirm={() => handleRemoveInProgress(params?.id || "")}
          open={params.action === "app-installation-remove"}
        />
      </DashboardCard>
    </ListPageLayout>
  );
};
